import type React from 'react';
import { useState } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import {
  Button,
  ButtonGroup,
  Divider,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Modal,
  Select,
  Textarea,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { css } from '@/styled-system/css';
import {
  DatarouterError,
  stringHelper,
  useRentalManagerMaintenanceConfigWebApiHandler_config,
  useRentalManagerUser,
} from '@zg-rentals/rental-manager-env';

import { useMaintenanceTrackerTaskUserWebApiHandler_create } from '@zg-rentals/ts-maintenance-tracker-client-react-query';

import { emitCloseTaskWithoutMakingTaskEvent, emitCreateNewTaskEvent } from '../clickstream/CreateNewTaskEvent';

const MAX_TASK_NAME_LENGTH = 100;
const MAX_TASK_NOTE_LENGTH = 150;

const CreateTaskSchema = Yup.object().shape({
  name: Yup.string().max(MAX_TASK_NAME_LENGTH).required('A name is required to create a task'),
  category: Yup.string(),
  isMultifamily: Yup.boolean(),
  listingAlias: Yup.string().required('A property is required to create a task'),
  unit: Yup.string().when('isMultifamily', { is: true, then: Yup.string().required('Select unit.') }),
  note: Yup.string().max(MAX_TASK_NOTE_LENGTH),
});

import { USE_TASKS_LIST } from '../constants/TaskConstants';
import PropertyField from '../components/PropertyField';

import type { ListingAlias, MaintenanceTaskCategory } from '@zg-rentals/ts-maintenance-tracker-client';

export type CreateFormFields = {
  category: string;
  cost?: number;
  note: string;
  feedModelId: string;
  isMultifamily: boolean;
  listingAlias: string;
  property?: string;
  name: string;
  unit?: string;
  vendor?: string;
};

const initialValues: CreateFormFields = {
  category: '',
  note: '',
  isMultifamily: false,
  listingAlias: '',
  property: '',
  name: '',
  unit: '',
  feedModelId: '',
};

type Props = {
  onClose: (newTaskId?: string) => void;
  listingAlias?: string;
};

export const CreateNewTaskModal = ({ onClose, listingAlias }: Props) => {
  const queryClient = useQueryClient();
  const { enqueueToast } = useToast();
  const { user } = useRentalManagerUser();

  const [nameCharCount, setNameCharCount] = useState(0);
  const [noteCharCount, setNoteCharCount] = useState(0);
  const [nameFocused, setNameFocused] = useState(false);
  const [noteFocused, setNoteFocused] = useState(false);

  const { mutate: createTask } = useMaintenanceTrackerTaskUserWebApiHandler_create();
  const { data: categoryOptions } = useRentalManagerMaintenanceConfigWebApiHandler_config({ staleTime: Infinity });

  const handleClosingWithoutMakingTask = () => {
    emitCloseTaskWithoutMakingTaskEvent({ user });
    onClose('');
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={CreateTaskSchema}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        setSubmitting(true);
        createTask(
          {
            postData: {
              // format to be UNDER_SCORE_ALL_CAPS
              // NOTE: `category` fallback `null` is needed https://zillowgroup.atlassian.net/browse/ZRM-14520
              category: (stringHelper.camelCaseToUnderScoreAllCaps(values.category) as MaintenanceTaskCategory) || null,
              ...(values.feedModelId && { feedModelId: values.feedModelId }),
              // double cast is necessary because TypeScript does not allow direct casting from a union type
              listingAlias: (listingAlias ?? values.listingAlias) as unknown as ListingAlias,
              ...(values.note && { note: values.note }),
              name: values.name,
            },
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: [USE_TASKS_LIST] });
              resetForm();
              emitCreateNewTaskEvent({ user });
            },
            onError: (error) => {
              enqueueToast(
                <Toast tone="critical">
                  {DatarouterError.getDisplayMessage(error, 'Error occurred with saving your maintenance task')}
                </Toast>,
              );
            },
            onSettled: (data) => {
              onClose(data?.maintenanceTaskId);
              setSubmitting(false);
            },
          },
        );
      }}
    >
      {({ isValid, isSubmitting, dirty, setFieldValue, setFieldTouched, errors, values, touched }) => (
        <Modal.Root
          onOpenChange={(open) => !open && handleClosingWithoutMakingTask()}
          size={{ base: 'fullScreen', md: 'sm' }}
          open={true}
        >
          <Modal.Portal>
            <Modal.Backdrop>
              <Modal.Content asChild>
                <Form placeholder="">
                  <Modal.Header>
                    <Heading level={1}>Create a task</Heading>
                  </Modal.Header>
                  <Divider />
                  <Modal.Body>
                    <FormField
                      required
                      error={(!!errors.name && touched.name) || nameCharCount > MAX_TASK_NAME_LENGTH}
                      label={<Label>Task name</Label>}
                      control={
                        <Field
                          as={Input}
                          name="name"
                          value={values.name}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (listingAlias) {
                              setFieldValue('listingAlias', listingAlias);
                            }

                            setFieldValue('name', e.target.value);
                            setNameCharCount(e.target.value.length);

                            setTimeout(() => setFieldTouched('name', true));
                          }}
                          onFocus={() => setNameFocused(true)}
                          onBlur={() => setNameFocused(false)}
                        />
                      }
                      description={
                        <FormHelp
                          css={nameFocused || nameCharCount > MAX_TASK_NAME_LENGTH ? { display: 'flex' } : undefined}
                        >
                          <span className={css({ flex: 1 })}>
                            {errors.name && touched.name
                              ? String(errors.name).charAt(0).toUpperCase() + String(errors.name).slice(1)
                              : nameCharCount > MAX_TASK_NAME_LENGTH
                                ? `Task name must be ${MAX_TASK_NAME_LENGTH} characters or fewer`
                                : ''}
                          </span>
                          {(nameFocused || nameCharCount > MAX_TASK_NAME_LENGTH) && (
                            <span
                              className={css({
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                marginLeft: 'auto',
                                paddingLeft: 'tighter',
                              })}
                            >
                              {nameCharCount.toLocaleString()}/{MAX_TASK_NAME_LENGTH} characters
                            </span>
                          )}
                        </FormHelp>
                      }
                    />
                    {!listingAlias && <PropertyField />}
                    <FormField
                      css={{ marginTop: 'default' }}
                      label={<Label optional>Category</Label>}
                      error={!!errors.category && touched.category}
                      control={
                        <Field
                          as={Select}
                          name="category"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setFieldValue('category', e.target.value);
                          }}
                        >
                          <option value=""></option>
                          {categoryOptions &&
                            categoryOptions.categories.map((option) => (
                              <option key={option.persistentString} value={option.persistentString}>
                                {option.display}
                              </option>
                            ))}
                        </Field>
                      }
                    />
                    <FormField
                      css={{ marginTop: 'default' }}
                      label={<Label optional>Notes</Label>}
                      error={(!!errors.note && touched.note) || noteCharCount > MAX_TASK_NOTE_LENGTH}
                      control={
                        <Field
                          as={Textarea}
                          name="note"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setFieldValue('note', e.target.value, true);
                            setNoteCharCount(e.target.value.length);
                          }}
                          onFocus={() => setNoteFocused(true)}
                          onBlur={() => setNoteFocused(false)}
                          css={{ resize: 'vertical' }}
                        />
                      }
                      description={
                        <FormHelp
                          css={noteFocused || noteCharCount > MAX_TASK_NOTE_LENGTH ? { display: 'flex' } : undefined}
                        >
                          <span className={css({ flex: 1 })}>
                            {errors.note && touched.note
                              ? String(errors.note).charAt(0).toUpperCase() + String(errors.note).slice(1)
                              : noteCharCount > MAX_TASK_NOTE_LENGTH
                                ? `Notes must be ${MAX_TASK_NOTE_LENGTH} characters or fewer`
                                : ''}
                          </span>
                          {(noteFocused || noteCharCount > MAX_TASK_NOTE_LENGTH) && (
                            <span
                              className={css({
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                marginLeft: 'auto',
                                paddingLeft: 'tighter',
                              })}
                            >
                              {noteCharCount.toLocaleString()}/{MAX_TASK_NOTE_LENGTH} characters
                            </span>
                          )}
                        </FormHelp>
                      }
                    />
                  </Modal.Body>
                  <Divider />
                  <Modal.Footer>
                    <ButtonGroup aria-label="Cancel or create task">
                      <TextButton type="button" onClick={handleClosingWithoutMakingTask}>
                        Cancel
                      </TextButton>
                      <Button
                        loading={isSubmitting}
                        disabled={!dirty || !isValid}
                        tone="brand"
                        emphasis="filled"
                        type="submit"
                      >
                        Create
                      </Button>
                    </ButtonGroup>
                  </Modal.Footer>
                  <Modal.CloseButton />
                </Form>
              </Modal.Content>
            </Modal.Backdrop>
          </Modal.Portal>
        </Modal.Root>
      )}
    </Formik>
  );
};
